import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { authApi } from '../services/api';

const STAFF_PRESENCE_PING_MS = 60 * 1000;

interface AuthContextType extends AuthState {
  login: (token: string, user: User) => void;
  logout: () => void;
  hasPermission: (entity: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Decoder le payload JWT et retourner le claim "exp" (en secondes Unix)
function getTokenExpiration(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const exp = getTokenExpiration(token);
  if (exp === null) return false;
  return Date.now() >= exp * 1000;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
  });
  const presencePingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (presencePingRef.current) {
      clearInterval(presencePingRef.current);
      presencePingRef.current = null;
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthState({ isAuthenticated: false, user: null, token: null });
    clearTimers();
  }, [clearTimers]);

  const sendPresencePing = useCallback(async () => {
    try {
      await authApi.ping();
    } catch {
      // L'intercepteur global gère déjà les 401 éventuels.
    }
  }, []);

  useEffect(() => {
    // Verifier le token au chargement
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      if (isTokenExpired(token)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return;
      }
      try {
        const user = JSON.parse(userStr);
        setAuthState({
          isAuthenticated: true,
          user,
          token,
        });
        void sendPresencePing();
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    return () => clearTimers();
  }, [clearTimers, sendPresencePing]);

  const login = (token: string, user: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setAuthState({
      isAuthenticated: true,
      user,
      token,
    });
  };

  const logout = () => {
    void authApi.logout().catch(() => undefined);
    clearSession();
  };

  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) {
      return undefined;
    }

    void sendPresencePing();
    presencePingRef.current = setInterval(() => {
      void sendPresencePing();
    }, STAFF_PRESENCE_PING_MS);

    return () => {
      if (presencePingRef.current) {
        clearInterval(presencePingRef.current);
        presencePingRef.current = null;
      }
    };
  }, [authState.isAuthenticated, authState.token, sendPresencePing]);

  const hasPermission = (entity: string, action: string): boolean => {
    if (!authState.user?.permissions) return false;
    return authState.user.permissions.includes(`${entity}:${action}`);
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
